import { useState, useEffect, useCallback, useMemo } from "react";
import { withTranslation } from "react-i18next";
import { Message, Checkbox, Divider, Segment, Input, Button, Icon } from "semantic-ui-react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import api from "api";
import actions from "actions";
import util from "utils/utils";
import styled from "styled-components";

import { CheckboxHeader, RadioHeader, EmptyBox } from "components/lib/UI";

const AudienceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const AudienceItem = styled.div`
  display: flex;
  justify-content: space-between;
  &:not(:last-of-type) {
    padding-bottom: 0.75rem;
    margin-bottom: 0.75rem;
    border-bottom: 1px solid lightgray;
  }
`;

const AudienceInfo = styled.div`
  h3 {
    margin: 0;
  }
  > span {
    font-size: 1.1rem;
  }
  .meta {
    color: gray;
    font-weight: 700;
  }
  .info {
    color: gray;
  }
`;

const AudienceActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  .ui.button {
    height: 40px;
    &:not(:last-of-type) {
      margin-right: 0.5rem;
    }
  }
`;

const AudienceChooser = ({ t, visibility, organisation, challenge, onVisibilityUpdated }) => {
  const dispatch = useDispatch();

  const [organisations, setOrganisations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [audienceType, setAudienceType] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [firstLoad, setFirstLoad] = useState(true);

  const [submissionCode, setSubmissionCode] = useState(challenge.shortcutSubmissions?.challengeCode || "");
  const [editingSubmissionCode, setEditingSubmissionCode] = useState(false);

  const getPostable = useCallback(() => {
    api.challenges.getPostable(challenge._id, (data) => {
      setOrganisations(data.organisations);
      setGroups(data.groups);
    });
  }, [challenge._id]);

  const setInitialVisibility = useCallback(() => {
    if (visibility && !util.isEmpty(visibility)) {
      if (
        (visibility.groups && visibility.groups.length > 0) ||
        !(
          visibility.organisations &&
          visibility.organisations.length === 1 &&
          visibility.organisations[0] === organisation._id
        )
      ) {
        setAudienceType("custom");
      } else setAudienceType("organisation");
    } else setAudienceType("organisation");
    if (!visibility) setAudienceType(null);
  }, [visibility, organisation]);

  const updateShortcutSubmission = useCallback(
    (data, success, failure) => {
      const currSettings = challenge.shortcutSubmissions || {};
      const update = { ...currSettings, ...data };
      api.challenges.update(
        challenge._id,
        { shortcutSubmissions: update },
        (updateData) => {
          success && success(updateData);
          dispatch(actions.challenges.receive(updateData));
        },
        failure,
      );
    },
    [challenge._id, dispatch, challenge.shortcutSubmissions],
  );

  useEffect(() => {
    if (firstLoad) {
      getPostable();
      setInitialVisibility();
      setFirstLoad(false);
    }
  }, [getPostable, setInitialVisibility, firstLoad]);

  const isInAudience = useCallback(
    (id) =>
      visibility &&
      ((visibility.organisations && visibility.organisations.indexOf(id) > -1) ||
        (visibility.groups && visibility.groups.indexOf(id) > -1)),
    [visibility],
  );

  const toggleAudience = useCallback(
    (type, id) => {
      const updatedGroups = [...(visibility?.groups || [])];
      const updatedOrganisations = [...(visibility?.organisations || [])];
      if (type === "organisation") {
        if (updatedOrganisations.indexOf(id) === -1) updatedOrganisations.push(id);
        else updatedOrganisations.splice(updatedOrganisations.indexOf(id), 1);
      }
      if (type === "group") {
        if (updatedGroups.indexOf(id) === -1) updatedGroups.push(id);
        else updatedGroups.splice(updatedGroups.indexOf(id), 1);
      }
      const updatedVisibility = { ...visibility, organisations: updatedOrganisations, groups: updatedGroups };
      onVisibilityUpdated(updatedVisibility);
    },
    [onVisibilityUpdated, visibility],
  );

  const togglePublicLink = useCallback(
    (on) => {
      const updatedVisibility = {
        ...visibility,
        publicLink: on,
        publicIdeaCreation: !on ? false : visibility?.publicIdeaCreation,
      };
      onVisibilityUpdated(updatedVisibility);
    },
    [visibility, onVisibilityUpdated],
  );

  const togglePublicIdeaCreation = useCallback(
    (on) => {
      const updatedVisibility = { ...visibility, publicIdeaCreation: on, publicIdeaCreationRequirement: "noAction" };
      onVisibilityUpdated(updatedVisibility);
    },
    [visibility, onVisibilityUpdated],
  );

  const togglePublicIdeaCreationRequirement = useCallback(
    (update) => {
      const updatedVisibility = { ...visibility, publicIdeaCreationRequirement: update };
      onVisibilityUpdated(updatedVisibility);
    },
    [visibility, onVisibilityUpdated],
  );

  const copyLink = useCallback(() => {
    if (window.navigator?.clipboard) {
      window.navigator.clipboard
        .writeText(
          `https://${challenge.ownerOrganisation ? challenge.ownerOrganisation.code : "app"}.simplydo.co.uk/challenges/${challenge._id}`,
        )
        .then(
          () => toast.success("Link copied to your clipboard"),
          () => toast.error("Unable to copy the link"),
        );
    } else {
      toast.error("Your browser does not support copying");
    }
  }, [challenge]);

  const changeAudienceType = useCallback(
    (type) => {
      setAudienceType(type);
      if (type === "organisation")
        onVisibilityUpdated({ organisations: [organisation._id], publicLink: visibility && visibility.publicLink });
      else if (type === "custom") onVisibilityUpdated(visibility);
    },
    [visibility, organisation, onVisibilityUpdated],
  );

  const filteredOrgs = orgFilter
    ? organisations.filter((o) => o.name.match(new RegExp(orgFilter, "i")))
    : organisations;
  const filteredGroups = groupFilter ? groups.filter((g) => g.name.match(new RegExp(groupFilter, "i"))) : groups;
  const smsDisabled = useMemo(() => challenge && challenge?.ipManagement?.isEnabled, [challenge]);

  const { enabledFeatures = [] } = organisation || {};
  return (
    <div>
      <h3>{t("challenge.settings.audience.info")}</h3>
      <Message info>{t("challenge.settings.audience.ideaCreators")}</Message>
      <Divider hidden />
      <RadioHeader
        as="h3"
        name="audienceRadio"
        value="organisation"
        label="Default"
        checked={audienceType === "organisation"}
        onClick={() => changeAudienceType("organisation")}
        header={t("challenge.settings.audience.organisation.title")}
        content={t("challenge.settings.audience.organisation.info", { orgName: organisation && organisation.name })}
      />
      <RadioHeader
        as="h3"
        name="audienceRadio"
        value="custom"
        checked={audienceType === "custom"}
        onClick={() => changeAudienceType("custom")}
        header={t("challenge.settings.audience.custom.title")}
        content={t("challenge.settings.audience.custom.info")}
      />

      <Divider hidden section />

      {audienceType === "custom" && (
        <div>
          <Segment attached="top">
            <h2>{t("challenge.settings.audience.custom.organisations")}</h2>
            <AudienceHeader>
              <p>{t("challenge.settings.audience.custom.organisationsInfo")}</p>
              <Input
                type="text"
                icon="search"
                size="mini"
                placeholder={t("generic.filter", { type: "organisations" })}
                onChange={(e) => setOrgFilter(e.target.value)}
                style={{ float: "right" }}
              />
            </AudienceHeader>

            {!filteredOrgs.length && (
              <EmptyBox
                style={{ marginTop: 15 }}
                title={t("challenge.settings.audience.custom.organisationsNone")}
                message={t("challenge.settings.audience.custom.organisationsNoneInfo")}
              />
            )}

            {filteredOrgs.map((org) => (
              <AudienceItem key={org._id}>
                <AudienceInfo>
                  {org._id !== organisation._id && <span className="meta">External organisation</span>}
                  <h3>{org.name}</h3>
                  <span className="info">{org.userCount} users</span>
                </AudienceInfo>
                <AudienceActions>
                  {!isInAudience(org._id) ? (
                    <Button
                      secondary
                      icon="user plus"
                      content={t("challenge.settings.audience.custom.organisationsAdd")}
                      onClick={() => toggleAudience("organisation", org._id)}
                    />
                  ) : (
                    <Button
                      color="orange"
                      basic
                      icon="user times"
                      content={t("challenge.settings.audience.custom.organisationsRemove")}
                      onClick={() => toggleAudience("organisation", org._id)}
                    />
                  )}
                </AudienceActions>
              </AudienceItem>
            ))}
          </Segment>

          {groups && groups.length > 0 && (
            <Segment attached>
              <h2>{t("generic.groups")}</h2>
              <AudienceHeader>
                <p>{t("challenge.settings.audience.custom.groupsInfo")}</p>
                <Input
                  type="text"
                  icon="search"
                  size="mini"
                  placeholder={t("generic.filter", { type: "groups" })}
                  onChange={(e) => setGroupFilter(e.target.value)}
                />
              </AudienceHeader>

              {!filteredGroups.length && (
                <EmptyBox
                  title={t("challenge.settings.audience.custom.groupsNone")}
                  message={t("challenge.settings.audience.custom.groupsNoneInfo")}
                />
              )}
              {filteredGroups.map((group) => (
                <>
                  <AudienceItem key={group._id}>
                    <AudienceInfo>
                      {group.organisation !== organisation._id && (
                        <span className="meta">{t("challenge.settings.audience.custom.groupsExternal")}</span>
                      )}
                      <h3>{group.name}</h3>
                      <span className="info">{`${group.userCount} users`}</span>
                    </AudienceInfo>
                    <AudienceActions>
                      {!isInAudience(group._id) ? (
                        <Button
                          secondary
                          icon="user plus"
                          content={t("challenge.settings.audience.custom.groupsAdd")}
                          onClick={() => toggleAudience("group", group._id)}
                        />
                      ) : (
                        <Button
                          color="orange"
                          basic
                          icon="user times"
                          content={t("challenge.settings.audience.custom.groupsRemove")}
                          onClick={() => toggleAudience("group", group._id)}
                        />
                      )}
                    </AudienceActions>
                  </AudienceItem>
                </>
              ))}
            </Segment>
          )}
        </div>
      )}
      {enabledFeatures?.indexOf("allowPublicChallenges") !== -1 ? (
        <>
          <Divider section />
          <CheckboxHeader
            willHideChildren
            header={t("challenge.settings.audience.publicLink.title")}
            description={t("challenge.settings.audience.publicLink.enable")}
            as="h3"
            checked={visibility && visibility.publicLink}
            onChange={(checked) => togglePublicLink(checked)}
          >
            <Message info>
              Challenges with a public link also become available on your organisation's{" "}
              <a
                href={`https://${challenge.ownerOrganisation ? challenge.ownerOrganisation.code : "app"}.simplydo.co.uk/challenges/public`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Public Challenges Page
              </a>
              .
            </Message>
            <Segment>
              {challenge && challenge._id && (
                <div>
                  <h4>Public link to this challenge</h4>
                  <Input
                    size="small"
                    fluid
                    value={`https://${challenge.ownerOrganisation ? challenge.ownerOrganisation.code : "app"}.simplydo.co.uk/challenges/${challenge._id}`}
                    readOnly
                    action={
                      <Button
                        color="teal"
                        icon="copy"
                        content={t("challenge.settings.audience.copyLink")}
                        onClick={copyLink}
                      />
                    }
                  />
                </div>
              )}
              <Message>{t("challenge.settings.audience.publicLink.info", { appName: util.appName() })}</Message>
            </Segment>

            {/* Challenges with IP management enabled cannot use public ideas */}
            {!challenge?.ipManagement?.isEnabled ? (
              <>
                <Divider hidden />
                <CheckboxHeader
                  willHideChildren
                  header="Public idea creation"
                  as="h4"
                  description="Additionally, allow non-members to submit an idea without being logged-in to an account."
                  checked={visibility && visibility.publicIdeaCreation}
                  onChange={(checked) => togglePublicIdeaCreation(checked)}
                >
                  <Segment>
                    <RadioHeader
                      as="h5"
                      name="publicCreationRadio"
                      value="noAction"
                      label={t("generic.default")}
                      checked={visibility && visibility.publicIdeaCreationRequirement === "noAction"}
                      onChange={() => togglePublicIdeaCreationRequirement("noAction")}
                      header="Prompt users to enter an email address after submission"
                      content="If selected, idea creators will be given the option to provide an email address when submitting an idea. They will not be required to enter an email address in order to submit their idea."
                    />

                    <RadioHeader
                      as="h5"
                      name="publicCreationRadio"
                      value="requireEmail"
                      checked={visibility && visibility.publicIdeaCreationRequirement === "requireEmail"}
                      onChange={() => togglePublicIdeaCreationRequirement("requireEmail")}
                      header="Require users to provide an email address to submit their idea"
                      content="If selected, idea creators will be required to provide an email address in order to submit their idea. They will not be required to complete their account setup, but they will receive challenge update communications."
                    />

                    <RadioHeader
                      as="h5"
                      name="publicCreationRadio"
                      value="accountCreation"
                      checked={visibility && visibility.publicIdeaCreationRequirement === "accountCreation"}
                      onChange={() => togglePublicIdeaCreationRequirement("accountCreation")}
                      header="Prompt user to create an account after submission"
                      content="If selected, idea creators will be asked to create an account when submitting their idea."
                    />
                  </Segment>
                </CheckboxHeader>
              </>
            ) : null}
          </CheckboxHeader>
        </>
      ) : null}
      <Divider hidden />
      <h4>SMS Shortcut submissions</h4>
      <p>Enabling SMS shortcut submissions allows users to submit ideas using only texts to a phone number.</p>
      {smsDisabled && (
        <Message warning>
          <Icon name="exclamation triangle" />
          SMS Shortcut submissions are disabled for this challenge. This is because IP management is enabled, you can
          update this setting{" "}
          <Link to={"ip"} style={{ textDecorationLine: "underline" }}>
            here
          </Link>
          .
        </Message>
      )}
      <CheckboxHeader
        disabled={smsDisabled}
        willHideChildren
        header="Enable SMS shortcut submissions"
        as="h5"
        description={`Anyone will be able to text the number ${import.meta.env.VITE_TWILIO_PHONE_NUMBER} with your challenge submission code, and be taken through a SMS-based idea creation process. Following creation of their idea, they will be offered the chance to create an account and assign the new idea to their new account. (Only text based fields can be created by SMS at this time)`}
        checked={challenge.shortcutSubmissions?.enabled}
        onChange={() => updateShortcutSubmission({ enabled: !challenge.shortcutSubmissions?.enabled })}
      >
        <>
          <h5>
            Challenge submission code <span style={{ color: "red" }}>*</span>
          </h5>
          <p>
            Users will be able to submit to your challenge using this identifier. Try to make this code as simple as
            possible so that users can easily interact with your challenge. Please note that the following keywords are
            reserved and cannot be used: CONTINUE, CANCEL, CHALLENGES
          </p>
          <div>
            <Input
              placeholder="LOTR"
              disabled={!editingSubmissionCode}
              value={submissionCode}
              onChange={(event) => setSubmissionCode(event.target.value)}
            />
            <Button
              primary
              content={editingSubmissionCode ? "Save" : "Update code"}
              disabled={
                editingSubmissionCode &&
                (submissionCode.length === 0 ||
                  ["CONTINUE", "CANCEL", "CHALLENGES"].includes(submissionCode.trim().toUpperCase()))
              }
              style={{ marginLeft: 5 }}
              onClick={() => {
                if (editingSubmissionCode) {
                  updateShortcutSubmission(
                    { challengeCode: submissionCode },
                    () => {
                      toast.success("Activated submission code.");
                      setEditingSubmissionCode(false);
                    },
                    () => {
                      toast.error("Code invalid or already in use.");
                    },
                  );
                } else {
                  setEditingSubmissionCode(true);
                }
              }}
            />
          </div>

          <h5>Enabled services</h5>
          <Checkbox
            checked={!!challenge.shortcutSubmissions?.externalServices?.sms?.enabled}
            onChange={() => {
              const services = challenge.shortcutSubmissions?.externalServices || {};
              const newServices = {
                ...services,
                sms: {
                  enabled: !challenge.shortcutSubmissions?.externalServices?.sms?.enabled,
                },
              };
              updateShortcutSubmission({ externalServices: newServices });
            }}
            label="SMS"
          />
        </>
      </CheckboxHeader>
    </div>
  );
};

const mapStateToProps = (state) => ({ user: state.user });
export default withTranslation()(connect(mapStateToProps)(AudienceChooser));
